import api from '../../../../services/api';

const getAuthHeader = () => ({});

// Type definitions
interface Sala {
  id?: string;
  // createSala's backend handler destructures the English `name` field, not `nome`.
  name: string;
  capacidade?: number;
  [key: string]: any;
}

interface Mesa {
  id?: string;
  // Not required: the backend never reads numero/capacidade for mesas, capacity
  // comes from min/max instead.
  numero?: number;
  capacidade?: number;
  sala_id: string;
  posicao_x?: number;
  posicao_y?: number;
  [key: string]: any;
}

interface Objeto {
  id?: string;
  nome: string;
  tipo: string;
  sala_id: string;
  posicao_x?: number;
  posicao_y?: number;
  [key: string]: any;
}

interface Reserva {
  id?: string | number;
  data: string;
  hora: string;
  nome_cliente: string;
  telefone_cliente: string;
  numero_pessoas: number;
  mesa_id: number | string | null;
  mesa_ids?: Array<number | string>;
  mesas?: Array<{
    id?: string | number | null;
    reserva_id?: string | number | null;
    mesa_id: number | string;
    ordem?: number | null;
  }>;
  sala_id: number | string;
  obs?: string;
  email_cliente?: string | null;
  nascimento_cliente?: string | null;
  cliente_id?: number | string | null;
  cliente?: {
    id?: number | string;
    nome?: string | null;
    telefone?: string | null;
  } | null;
  capacidade_excedida_confirmada?: boolean;
  [key: string]: any;
}

interface Horario {
  id?: string;
  dia_semana: number;
  turno: number;
  hora_inicio: string;
  hora_fim: string;
  aberto: boolean;
  antecedencia_min_horas?: number;
  [key: string]: any;
}

export interface BloqueioPayload {
  data: string;
  sala_id: string;
  turno?: number | null;
  horario_id?: number | null;
  motivo?: string | null;
}

export interface BloqueioResponse {
  id: number;
  data: string;
  sala_id: string | null;
  horario_id: number | null;
  motivo?: string | null;
  sala_nome?: string | null;
  turno?: number | null;
  hora_inicio?: string | null;
  hora_fim?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type WaitlistStatus = 'waiting' | 'ready' | 'notified' | 'seated' | 'cancelled';

export interface WaitlistEntry {
  id: string;
  restaurante_id?: number | string;
  sala_id?: string | null;
  sala_nome?: string | null;
  nome_cliente: string;
  telefone_cliente: string;
  numero_pessoas: number;
  obs?: string | null;
  data: string;
  status: WaitlistStatus;
  public_token?: string;
  position?: number | null;
  whatsapp_opt_in?: boolean;
  whatsapp_phone?: string | null;
  whatsapp_opt_in_url?: string | null;
  whatsapp_opt_in_message?: string | null;
  ready_at?: string | null;
  notified_at?: string | null;
  seated_at?: string | null;
  cancelled_at?: string | null;
  created_at: string;
  updated_at?: string;
}

// --- SALAS ---
export const prepareReservoDay = async (): Promise<void> => {
  await api.post('/reservo/ocupacoes/encerrar-anteriores', {}, { headers: getAuthHeader() });
};

export const fetchSalas = async () => {
  const res = await api.get('/reservo/salas', { headers: getAuthHeader() });
  return res.data;
};

export const createSala = async (data: Sala) => {
  const res = await api.post('/reservo/salas', data, { headers: getAuthHeader() });
  return res.data;
};

export const updateSala = async (id: string, data: Partial<Sala>) => {
  return await api.put(`/reservo/salas/${id}`, data, { headers: getAuthHeader() });
};

export const deleteSala = async (id: string, options?: { force?: boolean }) => {
  const config: Record<string, any> = { headers: getAuthHeader() };
  if (options?.force) {
    config.params = { force: true };
  }
  return await api.delete(`/reservo/salas/${id}`, config);
};

export interface SalaOcupacao {
  id: string;
  restaurante_id: number | string;
  sala_id: string;
  reserva_id?: string | null;
  waitlist_id?: string | null;
  nome_cliente?: string | null;
  telefone_cliente?: string | null;
  numero_pessoas_inicial: number;
  numero_pessoas_presentes: number;
  data: string;
  seated_at: string;
  left_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const fetchSalaOcupacoes = async (
  params?: { sala_id?: string; ativas?: boolean; data?: string }
): Promise<SalaOcupacao[]> => {
  const res = await api.get('/reservo/salas/ocupacoes', { headers: getAuthHeader(), params });
  return res.data;
};

export const ocuparSala = async (
  salaId: string,
  data: Record<string, any>
): Promise<SalaOcupacao> => {
  const res = await api.post(`/reservo/salas/${salaId}/ocupar`, data, { headers: getAuthHeader() });
  return res.data;
};

export const liberarSalaOcupacao = async (
  ocupacaoId: string,
  quantidadeSaida: number
): Promise<SalaOcupacao> => {
  const res = await api.post(
    `/reservo/salas/ocupacoes/${ocupacaoId}/liberar`,
    { quantidade_saida: quantidadeSaida },
    { headers: getAuthHeader() }
  );
  return res.data;
};

// --- MESAS ---
export const fetchMesas = async () => {
  const res = await api.get('/reservo/mesas', { headers: getAuthHeader() });
  return res.data;
};

export const createMesa = async (data: Mesa) => {
  const res = await api.post('/reservo/mesas', data, { headers: getAuthHeader() });
  return res.data;
};

export const updateMesa = async (id: string, data: Partial<Mesa>) => {
  return await api.put(`/reservo/mesas/${id}`, data, { headers: getAuthHeader() });
};

export const deleteMesa = async (id: string) => {
  return await api.delete(`/reservo/mesas/${id}`, { headers: getAuthHeader() });
};

export interface MesaOcupacao {
  id: number | string;
  mesa_id: number | string;
  sala_id: number | string;
  reserva_id?: number | string | null;
  numero_pessoas?: number | null;
  seated_at?: string;
  left_at?: string | null;
  [key: string]: any;
}

export const fetchMesaOcupacoes = async (
  params?: { sala_id?: string; mesa_id?: string; ativas?: boolean; data?: string }
): Promise<MesaOcupacao[]> => {
  const res = await api.get('/reservo/mesas/ocupacoes', { headers: getAuthHeader(), params });
  return res.data;
};

export const ocuparMesa = async (mesaId: string, data: Record<string, any> = {}): Promise<MesaOcupacao> => {
  const res = await api.post(`/reservo/mesas/${mesaId}/ocupar`, data, { headers: getAuthHeader() });
  return res.data;
};

export const selecionarMesa = async (mesaId: string, data: Record<string, any> = {}): Promise<MesaOcupacao> => {
  const res = await api.post(`/reservo/mesas/${mesaId}/selecionar`, data, { headers: getAuthHeader() });
  return res.data;
};

export const liberarMesa = async (mesaId: string, data: Record<string, any> = {}): Promise<MesaOcupacao> => {
  const res = await api.post(`/reservo/mesas/${mesaId}/liberar`, data, { headers: getAuthHeader() });
  return res.data;
};

// --- OBJETOS ---
export const fetchObjetos = async () => {
  const res = await api.get('/reservo/objetos', { headers: getAuthHeader() });
  return res.data;
};

export const createObjeto = async (data: Objeto) => {
  const res = await api.post('/reservo/objetos', data, { headers: getAuthHeader() });
  return res.data;
};

export const updateObjeto = async (id: string, data: Partial<Objeto>) => {
  return await api.put(`/reservo/objetos/${id}`, data, { headers: getAuthHeader() });
};

export const deleteObjeto = async (id: string) => {
  return await api.delete(`/reservo/objetos/${id}`, { headers: getAuthHeader() });
};

// --- RESERVAS ---
export const fetchReservas = async (params?: { data?: string }) => {
  const res = await api.get('/reservo/reservas', { headers: getAuthHeader(), params });
  return res.data;
};

export const createReserva = async (data: Reserva) => {
  const res = await api.post('/reservo/reservas', data, { headers: getAuthHeader() });
  return res.data;
};

export const updateReserva = async (id: string | number, data: Partial<Reserva>) => {
  return await api.put(`/reservo/reservas/${id}`, data, { headers: getAuthHeader() });
};

export const deleteReserva = async (id: string | number) => {
  return await api.delete(`/reservo/reservas/${id}`, { headers: getAuthHeader() });
};

// --- FILA DE ESPERA ---
export const fetchWaitlistEntries = async (
  params?: { data?: string; status?: WaitlistStatus; ativas?: boolean }
): Promise<WaitlistEntry[]> => {
  const res = await api.get('/reservo/fila', { headers: getAuthHeader(), params });
  return res.data;
};

export const notifyWaitlistEntry = async (id: string): Promise<WaitlistEntry> => {
  const res = await api.post(`/reservo/fila/${id}/avisar`, {}, { headers: getAuthHeader() });
  return res.data;
};

export const readyWaitlistEntry = async (id: string): Promise<WaitlistEntry> => {
  const res = await api.post(`/reservo/fila/${id}/pronto`, {}, { headers: getAuthHeader() });
  return res.data;
};

export const seatWaitlistEntry = async (
  id: string,
  data: Record<string, any> = {}
): Promise<WaitlistEntry> => {
  const res = await api.post(`/reservo/fila/${id}/sentar`, data, { headers: getAuthHeader() });
  return res.data;
};

export const cancelWaitlistEntry = async (id: string): Promise<WaitlistEntry> => {
  const res = await api.post(`/reservo/fila/${id}/remover`, {}, { headers: getAuthHeader() });
  return res.data;
};

// --- HORÁRIOS DE FUNCIONAMENTO ---
export const fetchHorarios = async () => {
  const res = await api.get('/reservo/horarios', {
    headers: getAuthHeader(),
    timeout: 15000,
  });
  return res.data;
};

export interface WeeklyHorarioInput {
  faixa_id?: string;
  dia_semana: number;
  hora_inicio: string;
  hora_fim: string;
  aberto: boolean;
}

export const replaceHorarios = async (horarios: WeeklyHorarioInput[]) => {
  const res = await api.put('/reservo/horarios', { horarios }, { headers: getAuthHeader() });
  return res.data;
};

export const fetchHorariosByTurno = async (turno: number) => {
  const res = await api.get(`/reservo/turnos/${turno}/horarios`, { headers: getAuthHeader() });
  return res.data;
};

export const createHorario = async (data: Horario) => {
  const res = await api.post('/reservo/horarios', data, { headers: getAuthHeader() });
  return res.data;
};

export const createHorariosForTurno = async (turno: number, horarios: any[]) => {
  const res = await api.post(`/reservo/turnos/${turno}/horarios`, { horarios }, { headers: getAuthHeader() });
  return res.data;
};

export const updateHorario = async (id: string, data: Partial<Horario>) => {
  return await api.put(`/reservo/horarios/${id}`, data, { headers: getAuthHeader() });
};

export const deleteHorario = async (id: string) => {
  return await api.delete(`/reservo/horarios/${id}`, { headers: getAuthHeader() });
};

export const deleteHorariosByTurno = async (turno: number) => {
  return await api.delete(`/reservo/turnos/${turno}/horarios`, { headers: getAuthHeader() });
};

// --- TURNOS ---
export const fetchTurnosDisponiveis = async () => {
  const res = await api.get('/reservo/turnos-disponiveis', { headers: getAuthHeader() });
  return res.data;
};

// --- INFORMAÇÕES DA SALA ---
export const fetchSalaInfo = async (salaId: string) => {
  const res = await api.get(`/reservo/salas/${salaId}/info`, { headers: getAuthHeader() });
  return res.data;
};

// --- HORÁRIOS DISPONÍVEIS POR SALA ---
export const fetchHorariosDisponiveisSala = async (salaId: string, data: string) => {
  const res = await api.get(`/reservo/salas/${salaId}/horarios-disponiveis?data=${data}`, {
    headers: getAuthHeader(),
    timeout: 15000,
  });
  return res.data;
};

// --- CONFIGURAÇÕES DO RESTAURANTE ---
export interface ReservoSettings {
  reservo_usar_reservas?: boolean;
  reservo_usar_fila?: boolean;
  reservo_usar_mapa_mesas?: boolean;
  reservo_confirmacao_restaurante?: boolean;
  reservo_confirmacao_cliente?: boolean;
  reservo_prazo_reserva_dias?: number;
  reservo_intervalo_minutos?: number | null;
  usar_feedbackhero_avaliacao_reservas?: boolean;
  checkin_slug?: string | null;
  [key: string]: any;
}

export const fetchRestaurantSettings = async (): Promise<ReservoSettings> => {
  const res = await api.get('/reservo/restaurant-settings', { headers: getAuthHeader() });
  return res.data;
};

export const updateRestaurantSettings = async (data: Record<string, any>): Promise<ReservoSettings> => {
  const res = await api.put('/reservo/restaurant-settings', data, { headers: getAuthHeader() });
  return res.data;
};

export const fetchAtendabotReservasSettings = async () => {
  const res = await api.get('/reservo/atendabot-integration', { headers: getAuthHeader() });
  const data = res.data || {};
  return {
    usar_reservo_reservas: data?.usar_reservo_reservas === true,
  };
};

export const updateAtendabotReservasSettings = async (data: {
  usar_reservo_reservas: boolean;
}) => {
  const res = await api.put('/reservo/atendabot-integration', data, { headers: getAuthHeader() });
  return res.data;
};

const fetchCoreModuleRule = async (moduleId: string) => {
  const res = await api.get('/config/core-config/module-rules', { headers: getAuthHeader() });
  const list = Array.isArray(res.data) ? res.data : [];
  return list.find((item: any) => item?.id === moduleId) || null;
};

const updateCoreModuleRuleSetting = async (moduleId: string, usarRegraGlobal: boolean) => {
  const res = await api.put(
    `/config/core-config/module-rules/${moduleId}`,
    { usar_regra_global: usarRegraGlobal },
    { headers: getAuthHeader() }
  );
  return res.data;
};

export const fetchReservoModuleRule = async () => fetchCoreModuleRule('reservo');

export const updateReservoModuleRule = async (usarRegraGlobal: boolean) =>
  updateCoreModuleRuleSetting('reservo', usarRegraGlobal);

// --- BLOQUEIOS ---
export const fetchBloqueios = async (): Promise<BloqueioResponse[]> => {
  const res = await api.get('/reservo/bloqueios', { headers: getAuthHeader() });
  return res.data;
};

export const createBloqueio = async (data: BloqueioPayload): Promise<BloqueioResponse> => {
  const res = await api.post('/reservo/bloqueios', data, { headers: getAuthHeader() });
  return res.data;
};

export const deleteBloqueio = async (id: string) => {
  return await api.delete(`/reservo/bloqueios/${id}`, { headers: getAuthHeader() });
};

export const fetchBloqueiosPublic = async (slug: string): Promise<BloqueioResponse[]> => {
  const res = await api.get(`/reservo/public/${slug}/bloqueios`);
  return res.data;
};

// --- PUBLIC API FUNCTIONS ---
export const fetchMesasPublic = async (slug: string, data?: string, hora?: string, numero_pessoas?: number) => {
  let url = `/reservo/public/${slug}/mesas`;
  const params = new URLSearchParams();

  if (data) params.append('data', data);
  if (hora) params.append('hora', hora);
  if (numero_pessoas) params.append('numero_pessoas', numero_pessoas.toString());

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const res = await api.get(url);
  return res.data;
};

export const fetchHorariosPublic = async (slug: string) => {
  const res = await api.get(`/reservo/public/${slug}/horarios`);
  return res.data;
};

export const fetchRestaurantSettingsPublic = async (slug: string) => {
  const res = await api.get(`/reservo/public/${slug}/settings`);
  return res.data;
};
